/**
 * Queue — system-wide job KPIs + a live job table, polled ~5s (WSRPDM-0013,
 * contract §3; table view WSRPDM-0043). Both `getSummary()`/`getJobs()` are
 * 2s server-cached; no server-side filters exist on `/queue/jobs` (contract
 * §3.2) — tenant and free-text filtering happen entirely client-side over
 * the polled job list, and so does the timeline ordering.
 *
 * Job types/statuses are never hardcoded (contract §6, no enumeration
 * endpoint): labels render via the WSRPDM-0011 registry, with its raw-enum
 * fallback for anything the catalog hasn't caught up to.
 *
 * Ordering (WSRPDM-0043, the Operator's core ask): most-recent-activity at
 * the top, queued jobs at the bottom — see the shared `compareQueueJobs`.
 * The `/queue/jobs` window itself is server-capped at 200 with no retention
 * logic on the console's side (contract §3.2) — this screen adds none.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { usePolling } from '@/hooks/usePolling';
import { useSetNavBadge } from '@/hooks/useNavBadges';
import { getJobs, getSummary, getUpcoming } from '@/api/wsr/queue';
import { listTenants } from '@/api/wsr/tenants';
import { jobTypeLabel, statusLabel } from '@/domain/registry';
import { KpiCard } from '@/components/ui';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { QueueJobView, UpcomingRun } from '@/api/wsr/types';
import { formatSecondsDuration } from './queue/duration';
import { jobTimelineText } from './queue/jobTimeline';
import { ActiveJobsTable, UpcomingTable } from './queue/QueueTables';
import { compareQueueJobs, queueKindValue, type QueueTranslateFn } from './queue/tableValues';

const ACTIVE_STATUSES = new Set(['RUNNING', 'CLAIMED']);
const UPCOMING_RELATIVE_HORIZON_SECONDS = 24 * 60 * 60;

/**
 * `registry.ts`'s `TFn` is label-only (`(key, fallback?) => string`) —
 * narrower than `useCopy()`'s actual 3-arg `t`, which satisfies it
 * structurally. `jobTimelineText` needs the wider shape for its
 * `{{duration}}`/`{{time}}` interpolation.
 */
type TranslateFn = QueueTranslateFn;

function upcomingNextValue(run: UpcomingRun, t: TranslateFn, locale: string): string {
  const dueAt = new Date(run.nextDueAt);
  const secondsUntil = Math.floor((dueAt.getTime() - Date.now()) / 1000);
  if (secondsUntil <= 0) return t('queue.upcoming.dueNow', 'due now');
  if (secondsUntil < UPCOMING_RELATIVE_HORIZON_SECONDS) {
    return t('queue.upcoming.in', 'in {{duration}}', {
      duration: formatSecondsDuration(secondsUntil),
    });
  }
  return dueAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

async function getUpcomingSnapshot() {
  const data = await getUpcoming();
  return { data, nowMs: Date.now() };
}

/**
 * The per-row searchable string the free-text filter matches against —
 * both raw values (an operator may paste a raw enum from a log line) and
 * rendered labels (an operator types "user sync", not `USER_SYNC`). The
 * status label is read from the registry's own `statusLabel` — the same
 * helper `StatusPill` renders from — so the filter and the cell can never
 * disagree.
 */
function buildSearchText(job: QueueJobView, generatedAt: string, t: TranslateFn): string {
  return [
    String(job.id),
    job.tenantSlug,
    job.jobType,
    jobTypeLabel(job.jobType, t),
    job.runKind ?? '',
    queueKindValue(job.runKind, t),
    job.status,
    statusLabel(job.status, t),
    jobTimelineText(job, generatedAt, t),
  ]
    .join(' ')
    .toLowerCase();
}

export function Queue() {
  const { t } = useCopy();
  // `useCopy()`'s `t` is a fresh closure every render (see its doc comment),
  // so it can't be a `useMemo` dependency without defeating the memo on
  // every render (WSRPDM-0012 hit this same instability). `i18n.language`
  // is the stable primitive that actually captures when `t`'s output can
  // change — used as the dependency for `searchTextById` below instead.
  const { i18n } = useTranslation();
  const summaryPolling = usePolling(getSummary, { intervalMs: 5000, pauseWhenHidden: true });
  const jobsPolling = usePolling(getJobs, { intervalMs: 5000, pauseWhenHidden: true });
  const upcomingPolling = usePolling(getUpcomingSnapshot, {
    intervalMs: 5000,
    pauseWhenHidden: true,
  });

  const [tenantOptions, setTenantOptions] = useState<string[] | null>(null);
  const [tenantRosterFailed, setTenantRosterFailed] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [textFilter, setTextFilter] = useState('');

  const summary = summaryPolling.data;
  const jobsData = jobsPolling.data;
  // Memoized so a null `jobsData` (before the first poll settles) doesn't
  // hand every downstream useMemo a fresh `[]` identity on every render.
  const jobs = useMemo(() => jobsData?.jobs ?? [], [jobsData]);
  const generatedAt = jobsData?.generatedAt ?? new Date(0).toISOString();

  // Nav badge — prefer the summary's inFlight (contract §3.1: CLAIMED +
  // RUNNING); fall back to counting the job list once it's loaded; publish
  // nothing (null) until at least one of the two polls has data, so the
  // badge doesn't flash a false "0" before the first response lands.
  const activeJobsCount = jobs.filter((job) => ACTIVE_STATUSES.has(job.status)).length;
  const badgeCount = summary ? summary.inFlight : jobsData ? activeJobsCount : null;
  useSetNavBadge('queue', badgeCount);

  // The tenant roster changes rarely, so load it once independently of the
  // 5s queue polls. A successful roster is authoritative for selection
  // validity; a failed request stays silent and leaves the selection alone.
  useEffect(() => {
    let cancelled = false;
    listTenants()
      .then((res) => {
        if (cancelled) return;
        const slugs = res.tenants.map((tenant) => tenant.tenantSlug).sort();
        setTenantOptions(slugs);
        setTenantFilter((current) =>
          current !== 'all' && !slugs.includes(current) ? 'all' : current,
        );
      })
      .catch(() => {
        if (!cancelled) setTenantRosterFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fallbackTenantOptions = useMemo(() => {
    const slugs = new Set(jobs.map((job) => job.tenantSlug));
    return Array.from(slugs).sort();
  }, [jobs]);
  const displayedTenantOptions = tenantRosterFailed ? fallbackTenantOptions : (tenantOptions ?? []);

  // Built once per poll tick (not on every filter-box keystroke, which only
  // changes `textFilter`/`tenantFilter` state) so a 5s poll's 200 rows don't
  // get their search text rebuilt on every render. Depends on
  // `i18n.language`, not `t` — `t` is a new closure every render (see the
  // note above), so keying this memo on it would rebuild the map on every
  // keystroke and defeat the memoization entirely. `t` is still read fresh
  // from this same render's closure (not a stale ref), so a language switch
  // is picked up correctly on the render that changes `i18n.language` —
  // deliberately not `eslint-disable`d per the WSRPDM-0012 precedent
  // (`docs/deliveries/WSRPDM-0012-...-delivery.md`): the leftover
  // `react-hooks/exhaustive-deps` warning here is expected, not a mistake.
  const searchTextById = useMemo(() => {
    const map = new Map<number, string>();
    for (const job of jobs) map.set(job.id, buildSearchText(job, generatedAt, t));
    return map;
  }, [jobs, generatedAt, i18n.language]);

  const filteredJobs = useMemo(() => {
    const query = textFilter.trim().toLowerCase();
    return jobs.filter((job) => {
      if (tenantFilter !== 'all' && job.tenantSlug !== tenantFilter) return false;
      if (query !== '' && !(searchTextById.get(job.id) ?? '').includes(query)) return false;
      return true;
    });
  }, [jobs, tenantFilter, textFilter, searchTextById]);

  // Sort a copy — never mutate the polled array.
  const orderedJobs = useMemo(() => [...filteredJobs].sort(compareQueueJobs), [filteredJobs]);
  const filteredUpcoming = useMemo(
    () =>
      (upcomingPolling.data?.data.upcoming ?? []).filter(
        (run) => tenantFilter === 'all' || run.tenantSlug === tenantFilter,
      ),
    [upcomingPolling.data, tenantFilter],
  );
  const nowMs = upcomingPolling.data?.nowMs ?? 0;
  const overdueRuns = filteredUpcoming.filter((run) => run.overdue === true);
  const upcomingRuns = filteredUpcoming.filter((run) => run.overdue !== true);

  const loadErrorMessage =
    summaryPolling.error || jobsPolling.error
      ? t('queue.loadError', 'Could not load the queue.')
      : null;

  const emptyMessage =
    jobs.length > 0 && filteredJobs.length === 0
      ? t('queue.emptyFiltered', 'No jobs match the current filters.')
      : t('queue.empty', 'No active jobs.');

  const isInitialLoading = summaryPolling.loading && jobsPolling.loading;

  const handleRetry = () => {
    summaryPolling.refresh();
    jobsPolling.refresh();
  };

  return (
    <Box>
      <Typography variant="h1" component="h1" gutterBottom>
        {t('page.queue.title', 'Queue')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('page.queue.subtitle', 'Ingestion queue depth, activity and upcoming work.')}
      </Typography>

      {loadErrorMessage ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Typography
              component="button"
              variant="caption"
              onClick={handleRetry}
              sx={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
              }}
            >
              {t('common.retry', 'Retry')}
            </Typography>
          }
        >
          {loadErrorMessage}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label={t('queue.kpi.pending', 'Pending')} value={summary?.pending ?? '—'} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label={t('queue.kpi.inFlight', 'In flight')} value={summary?.inFlight ?? '—'} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t('queue.kpi.oldestPending', 'Oldest pending')}
            value={
              summary
                ? summary.oldestPendingAgeSeconds !== null
                  ? formatSecondsDuration(summary.oldestPendingAgeSeconds)
                  : '—'
                : '—'
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t('queue.kpi.failedLast24h', 'Failed (24h)')}
            value={summary?.failedLast24h ?? '—'}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          mb: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <TextField
            size="small"
            label={t('queue.filters.searchLabel', 'Filter jobs')}
            placeholder={t('queue.filters.searchPlaceholder', 'Tenant, job type, status…')}
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            sx={{ minWidth: 260 }}
            slotProps={{
              input: {
                endAdornment:
                  textFilter !== '' ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label={t('common.clear', 'Clear')}
                        onClick={() => setTextFilter('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
              },
            }}
          />
          <TextField
            select
            size="small"
            label={t('queue.filters.tenantLabel', 'Tenant')}
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">{t('queue.filters.allTenants', 'All tenants')}</MenuItem>
            {displayedTenantOptions.map((slug) => (
              <MenuItem key={slug} value={slug}>
                {slug}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('queue.filters.pollNote', 'Live — updates every ~5s')}
        </Typography>
      </Stack>

      {isInitialLoading ? <LoadingSpinner /> : null}

      {!isInitialLoading ? (
        <ActiveJobsTable
          rows={orderedJobs}
          generatedAt={generatedAt}
          emptyMessage={emptyMessage}
          locale={i18n.language}
          t={t}
        />
      ) : null}

      {upcomingPolling.data ? (
        <Box sx={{ mt: 4 }}>
          {filteredUpcoming.length === 0 ? (
            <>
              <Typography id="upcoming-heading" variant="h2" component="h2" gutterBottom>
                {t('queue.upcoming.title', 'Upcoming')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('queue.upcoming.empty', 'Nothing scheduled.')}
              </Typography>
            </>
          ) : (
            <Stack spacing={3}>
              {overdueRuns.length > 0 ? (
                <Box>
                  <Typography id="overdue-heading" variant="h2" component="h2" gutterBottom>
                    {t('queue.upcoming.overdueTitle', 'Overdue')}
                  </Typography>
                  <UpcomingTable
                    rows={overdueRuns}
                    headingId="overdue-heading"
                    nextValue={(run) =>
                      t('queue.upcoming.overdueBy', '{{duration}} overdue', {
                        duration: formatSecondsDuration(
                          Math.floor((nowMs - new Date(run.nextDueAt).getTime()) / 1000),
                        ),
                      })
                    }
                    t={t}
                  />
                </Box>
              ) : null}
              {upcomingRuns.length > 0 ? (
                <Box>
                  <Typography id="upcoming-heading" variant="h2" component="h2" gutterBottom>
                    {t('queue.upcoming.title', 'Upcoming')}
                  </Typography>
                  <UpcomingTable
                    rows={upcomingRuns}
                    headingId="upcoming-heading"
                    nextValue={(run) => upcomingNextValue(run, t, i18n.language)}
                    t={t}
                  />
                </Box>
              ) : null}
            </Stack>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
